import { Component } from '@theme/component';

/**
 * A small, self-contained lightbox for the alpaca gallery. Deliberately
 * avoids the native <dialog> element and the theme's shared dialog
 * animation/timing logic (both caused inconsistent open/close behavior for
 * this simple image-only use case) in favor of a plain overlay toggled by
 * an `open` attribute.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overlay - The overlay element.
 * @property {HTMLImageElement} image - The full-size image element.
 * @property {HTMLButtonElement} closeButton - The close button.
 *
 * @extends Component<Refs>
 */
class AlpacaGalleryLightbox extends Component {
  requiredRefs = ['overlay', 'image', 'closeButton'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.closeButton.addEventListener('click', () => this.close());
    this.refs.overlay.addEventListener('click', (event) => {
      if (event.target === this.refs.overlay) this.close();
    });

    this.#handleKeydown = (event) => {
      if (event.key === 'Escape') this.close();
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  /** @type {(event: KeyboardEvent) => void} */
  #handleKeydown = () => {};

  #scrollY = 0;

  /**
   * Shows the lightbox with the image from the clicked thumbnail.
   *
   * @param {PointerEvent} event - The click event from the thumbnail button.
   */
  showImage(event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    const src = button?.dataset.fullSrc;

    if (button && src) {
      this.refs.image.src = src;
      this.refs.image.alt = button.dataset.alt ?? '';
    }

    this.open();
  }

  open() {
    if (this.refs.overlay.hasAttribute('open')) return;

    this.refs.overlay.setAttribute('open', '');
    this.#scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;
    document.body.style.width = '100%';
    document.addEventListener('keydown', this.#handleKeydown);
    this.refs.closeButton.focus();
  }

  close() {
    if (!this.refs.overlay.hasAttribute('open')) return;

    this.refs.overlay.removeAttribute('open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo({ top: this.#scrollY, behavior: 'instant' });
    document.removeEventListener('keydown', this.#handleKeydown);
  }
}

if (!customElements.get('alpaca-gallery-lightbox')) {
  customElements.define('alpaca-gallery-lightbox', AlpacaGalleryLightbox);
}
